import { useState, useEffect } from 'react';
import type {
  ComplianceItem, ComplianceStatus, ComplianceCategory,
  DataRequest, ConsentRecord, RetentionPolicy, RetentionJob,
  MinorDetection, BreachEvent, AuditLogEntry, ThirdPartyShare,
} from '../../../types/dataops';
import {
  getAllComplianceItems, saveComplianceItem,
  getAllDataRequests, getAllConsentRecords,
  getAllRetentionPolicies, getAllRetentionJobs,
  getAllMinorDetections, getAllBreachEvents,
  getAuditLog, getAllThirdPartyShares,
} from '../../../services/firestore/dataopsService';
import { getAllPolicies } from '../../../services/firestore/regulatoryService';
import type { RegulatoryPolicy } from '../../../types/regulatory';

// ── BRAND PALETTE (light theme — matches landing page) ──
const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  accent: '#E11D48', accentSoft: '#FFF1F2', accentLight: '#FB7185',
  bg: '#FFFFFF', bgSoft: '#F8FAFC',
  border: '#E4E4E7', borderLight: '#F4F4F5',
  btn: '#18181B', btnHover: '#27272A',
  success: '#059669', successBg: '#D1FAE5',
  amber: '#D97706', amberBg: '#FEF3C7',
  red: '#DC2626', redBg: '#FEF2F2',
  orange: '#EA580C', orangeBg: '#FFF7ED',
  blue: '#2563EB', blueBg: '#DBEAFE',
};

// ── STATUS CONFIG ──
const STATUS_CONFIG: Record<ComplianceStatus, { label: string; color: string; bg: string }> = {
  not_started: { label: 'NOT STARTED', color: C.red, bg: C.redBg },
  in_progress: { label: 'IN PROGRESS', color: C.amber, bg: C.amberBg },
  operational: { label: 'OPERATIONAL', color: C.success, bg: C.successBg },
  needs_attention: { label: 'NEEDS ATTENTION', color: C.orange, bg: C.orangeBg },
};

const CATEGORY_CONFIG: Record<ComplianceCategory, { label: string; icon: string }> = {
  user_data_rights: { label: 'User Data Rights', icon: '\uD83D\uDD10' },
  child_safety: { label: 'Child Safety', icon: '\uD83D\uDEE1\uFE0F' },
  consent_management: { label: 'Consent Management', icon: '\u2705' },
  data_retention: { label: 'Data Retention', icon: '\uD83D\uDDD1\uFE0F' },
  security_audit: { label: 'Security & Audit', icon: '\uD83D\uDD12' },
  third_party_sharing: { label: 'Third-Party Sharing', icon: '\uD83E\uDD1D' },
  data_inventory: { label: 'Data Inventory', icon: '\uD83D\uDDC2\uFE0F' },
};

const TABS = [
  { key: 'policies', label: 'Policies' },
  { key: 'requests', label: 'Requests' },
  { key: 'consent', label: 'Consent' },
  { key: 'retention', label: 'Retention' },
  { key: 'security', label: 'Security' },
  { key: 'audit', label: 'Audit' },
] as const;

type TabKey = typeof TABS[number]['key'];

// ── MAIN COMPONENT ──
export function DataOpsDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>('policies');
  const [selectedItem, setSelectedItem] = useState<ComplianceItem | null>(null);

  // Data
  const [complianceItems, setComplianceItems] = useState<ComplianceItem[]>([]);
  const [requests, setRequests] = useState<DataRequest[]>([]);
  const [consentRecords, setConsentRecords] = useState<ConsentRecord[]>([]);
  const [retentionPolicies, setRetentionPolicies] = useState<RetentionPolicy[]>([]);
  const [retentionJobs, setRetentionJobs] = useState<RetentionJob[]>([]);
  const [minorDetections, setMinorDetections] = useState<MinorDetection[]>([]);
  const [breachEvents, setBreachEvents] = useState<BreachEvent[]>([]);
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [thirdPartyShares, setThirdPartyShares] = useState<ThirdPartyShare[]>([]);
  const [policies, setPolicies] = useState<RegulatoryPolicy[]>([]);

  // Detail panel edits
  const [editStatus, setEditStatus] = useState<ComplianceStatus>('not_started');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [ci, dr, cr, rp, rj, md, be, al, tp, pol] = await Promise.all([
          getAllComplianceItems(),
          getAllDataRequests(),
          getAllConsentRecords(),
          getAllRetentionPolicies(),
          getAllRetentionJobs(),
          getAllMinorDetections(),
          getAllBreachEvents(),
          getAuditLog(100),
          getAllThirdPartyShares(),
          getAllPolicies(),
        ]);
        setComplianceItems(ci);
        setRequests(dr);
        setConsentRecords(cr);
        setRetentionPolicies(rp);
        setRetentionJobs(rj);
        setMinorDetections(md);
        setBreachEvents(be);
        setAuditEntries(al);
        setThirdPartyShares(tp);
        setPolicies(pol);
      } catch (err) {
        console.error('DataOps load error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── COMPUTED METRICS ──
  const openRequests = requests.filter((r) => !['completed', 'denied'].includes(r.status)).length;
  const completedRequests = requests.filter((r) => r.status === 'completed');
  const slaCompliance = completedRequests.length > 0
    ? Math.round((completedRequests.filter((r) => !r.slaBreached).length / completedRequests.length) * 100)
    : null;
  const breachScanDays = breachEvents.length > 0
    ? Math.floor((Date.now() - new Date(breachEvents[0].detectedAt).getTime()) / 86400000)
    : null;
  const minorThisMonth = minorDetections.filter((d) => {
    const det = new Date(d.detectedAt);
    const now = new Date();
    return det.getMonth() === now.getMonth() && det.getFullYear() === now.getFullYear();
  }).length;

  // Compliance summary
  const totalItems = complianceItems.length;
  const operationalCount = complianceItems.filter((i) => i.status === 'operational').length;
  const inProgressCount = complianceItems.filter((i) => i.status === 'in_progress').length;
  const notStartedCount = complianceItems.filter((i) => i.status === 'not_started').length;
  const needsAttentionCount = complianceItems.filter((i) => i.status === 'needs_attention').length;
  const overallPct = totalItems > 0 ? Math.round((operationalCount / totalItems) * 100) : 0;

  // Sort by status priority (worst first), then by phase
  const STATUS_PRIORITY: Record<ComplianceStatus, number> = {
    not_started: 0,
    needs_attention: 1,
    in_progress: 2,
    operational: 3,
  };

  const sortedItems = [...complianceItems].sort((a, b) => {
    const sp = STATUS_PRIORITY[a.status] - STATUS_PRIORITY[b.status];
    if (sp !== 0) return sp;
    return a.implementationPhase - b.implementationPhase;
  });

  // Group by category (using sorted items)
  const grouped = sortedItems.reduce<Record<string, ComplianceItem[]>>((acc, item) => {
    (acc[item.category] ??= []).push(item);
    return acc;
  }, {});

  function openDetail(item: ComplianceItem) {
    setSelectedItem(item);
    setEditStatus(item.status);
    setEditNotes(item.notes);
  }

  async function handleSaveDetail() {
    if (!selectedItem) return;
    setSaving(true);
    const updated: ComplianceItem = {
      ...selectedItem,
      status: editStatus,
      notes: editNotes,
      lastVerified: editStatus === 'operational' ? new Date().toISOString().slice(0, 10) : selectedItem.lastVerified,
      updatedAt: new Date().toISOString().slice(0, 10),
    };
    try {
      await saveComplianceItem(updated);
      setComplianceItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      setSelectedItem(updated);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="do" style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>
        <div style={{ fontSize: 18 }}>Loading Data Operations...</div>
      </div>
    );
  }

  return (
    <div className="do" style={{ fontFamily: "'DM Sans', -apple-system, sans-serif", color: C.text, maxWidth: 1200, margin: '0 auto' }}>
      <style>{styles}</style>

      {/* ── HEADER ── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>
            <span style={{ background: 'linear-gradient(135deg, #C9A96E, #B8944F)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>HEMOLOGIX</span>
          </h1>
          <span className="do-badge">Data Operations</span>
        </div>
        <p style={{ color: C.textMuted, fontSize: 14, margin: 0 }}>
          Compliance infrastructure status — if we promise it in a policy, it must work here.
        </p>
      </div>

      {/* ── METRIC CARDS ── */}
      <div className="do-metrics">
        <MetricCard label="Open Requests" value={requests.length > 0 ? String(openRequests) : '--'} color={openRequests > 0 ? C.amber : C.success} />
        <MetricCard label="SLA Compliance" value={slaCompliance !== null ? `${slaCompliance}%` : '--'} color={slaCompliance !== null && slaCompliance < 90 ? C.red : C.success} />
        <MetricCard label="Active Consent" value={consentRecords.length > 0 ? `${consentRecords.filter((r) => r.action === 'granted').length}` : '--'} color={C.blue} />
        <MetricCard label="Breach Scan" value={breachScanDays !== null ? `${breachScanDays}d ago` : '--'} color={breachScanDays !== null && breachScanDays > 1 ? C.amber : C.success} />
        <MetricCard label="Minor Detections" value={minorDetections.length > 0 ? String(minorThisMonth) : '--'} color={minorThisMonth > 0 ? C.red : C.success} />
      </div>

      {/* ── COMPLIANCE MATRIX ── */}
      <div className="do-section">
        <div className="do-section-header">
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0, fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>Compliance Matrix</h2>
          <div style={{ display: 'flex', gap: 16, fontSize: 13, color: C.textMuted }}>
            <span><span className="do-dot" style={{ background: C.success }} /> {operationalCount} operational</span>
            <span><span className="do-dot" style={{ background: C.amber }} /> {inProgressCount} in progress</span>
            <span><span className="do-dot" style={{ background: C.red }} /> {notStartedCount} not started</span>
            {needsAttentionCount > 0 && <span><span className="do-dot" style={{ background: C.orange }} /> {needsAttentionCount} needs attention</span>}
          </div>
        </div>

        {/* Progress bar */}
        <div className="do-progress-wrap">
          <div className="do-progress-bar" style={{ width: `${overallPct}%` }} />
          <span className="do-progress-label">{overallPct}% operational ({operationalCount}/{totalItems})</span>
        </div>

        {totalItems === 0 ? (
          <div className="do-empty">
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 500 }}>No compliance items loaded</div>
            <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4 }}>Go to Seed Data and click "Seed Data Ops" to load the compliance checklist.</div>
          </div>
        ) : (
          <div className="do-matrix">
            {Object.entries(CATEGORY_CONFIG)
            .filter(([catKey]) => grouped[catKey] && grouped[catKey].length > 0)
            .sort(([a], [b]) => {
              const worstStatus = (items: ComplianceItem[]) => Math.min(...items.map((i) => STATUS_PRIORITY[i.status]));
              return worstStatus(grouped[a]) - worstStatus(grouped[b]);
            })
            .map(([catKey, catConf]) => {
              const items = grouped[catKey];
              return (
                <div key={catKey} className="do-cat-group">
                  <div className="do-cat-header">
                    <span>{catConf.icon}</span>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{catConf.label}</span>
                    <span style={{ fontSize: 12, color: C.textMuted }}>
                      {items.filter((i) => i.status === 'operational').length}/{items.length}
                    </span>
                  </div>
                  {items.map((item) => {
                    const sc = STATUS_CONFIG[item.status];
                    return (
                      <div
                        key={item.id}
                        className={`do-row ${selectedItem?.id === item.id ? 'do-row-active' : ''}`}
                        onClick={() => openDetail(item)}
                      >
                        <span className="do-dot" style={{ background: sc.color, flexShrink: 0 }} />
                        <span className="do-row-label">{item.label}</span>
                        <span className="do-pill" style={{ color: sc.color, background: sc.bg }}>{sc.label}</span>
                        <span className="do-row-regs">
                          {item.regulatoryBasis.map((r) => (
                            <span key={r} className="do-reg-badge">{r}</span>
                          ))}
                        </span>
                        <span className="do-row-sla">{item.sla || '--'}</span>
                        <span className="do-row-phase">P{item.implementationPhase}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── TABS ── */}
      <div className="do-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`do-tab ${activeTab === tab.key ? 'do-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.key === 'requests' && requests.length > 0 && (
              <span className="do-tab-badge">{openRequests}</span>
            )}
            {tab.key === 'security' && (breachEvents.length > 0 || minorDetections.length > 0) && (
              <span className="do-tab-badge">{breachEvents.length + minorDetections.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── TAB CONTENT ── */}
      <div className="do-tab-content">
        {activeTab === 'policies' && <PoliciesTab policies={policies} complianceItems={complianceItems} />}
        {activeTab === 'requests' && <RequestsTab requests={requests} />}
        {activeTab === 'consent' && <ConsentTab records={consentRecords} />}
        {activeTab === 'retention' && <RetentionTab policies={retentionPolicies} jobs={retentionJobs} />}
        {activeTab === 'security' && <SecurityTab breaches={breachEvents} minors={minorDetections} />}
        {activeTab === 'audit' && <AuditTab entries={auditEntries} shares={thirdPartyShares} />}
      </div>

      {/* ── DETAIL PANEL ── */}
      {selectedItem && (
        <div className="do-panel-overlay" onClick={() => setSelectedItem(null)}>
          <div className="do-panel" onClick={(e) => e.stopPropagation()}>
            <div className="do-panel-header">
              <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>{selectedItem.label}</h3>
              <button className="do-panel-close" onClick={() => setSelectedItem(null)}>&times;</button>
            </div>

            <div className="do-panel-body">
              <div className="do-field">
                <label className="do-field-label">Description</label>
                <p style={{ fontSize: 13, color: C.textSec, margin: 0, lineHeight: 1.5 }}>{selectedItem.description}</p>
              </div>

              <div className="do-field-row">
                <div className="do-field">
                  <label className="do-field-label">Status</label>
                  <select
                    className="do-select"
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value as ComplianceStatus)}
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="operational">Operational</option>
                    <option value="needs_attention">Needs Attention</option>
                  </select>
                </div>
                <div className="do-field">
                  <label className="do-field-label">Owner</label>
                  <div style={{ fontSize: 13, color: C.textSec }}>{selectedItem.owner}</div>
                </div>
              </div>

              <div className="do-field-row">
                <div className="do-field">
                  <label className="do-field-label">SLA</label>
                  <div style={{ fontSize: 13, color: C.textSec }}>{selectedItem.sla || 'None'}</div>
                </div>
                <div className="do-field">
                  <label className="do-field-label">Phase</label>
                  <div style={{ fontSize: 13, color: C.textSec }}>Phase {selectedItem.implementationPhase}</div>
                </div>
              </div>

              <div className="do-field">
                <label className="do-field-label">Policy Reference</label>
                <div style={{ fontSize: 13, color: C.textSec }}>{selectedItem.policyRef}</div>
              </div>

              <div className="do-field">
                <label className="do-field-label">Regulatory Basis</label>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {selectedItem.regulatoryBasis.map((r) => (
                    <span key={r} className="do-reg-badge">{r}</span>
                  ))}
                </div>
              </div>

              {selectedItem.firestoreCollection && (
                <div className="do-field">
                  <label className="do-field-label">Firestore Collection</label>
                  <code style={{ fontSize: 12, color: C.accent }}>{selectedItem.firestoreCollection}</code>
                </div>
              )}

              {selectedItem.cloudFunction && (
                <div className="do-field">
                  <label className="do-field-label">Cloud Function</label>
                  <code style={{ fontSize: 12, color: C.accent }}>{selectedItem.cloudFunction}</code>
                </div>
              )}

              <div className="do-field">
                <label className="do-field-label">Last Verified</label>
                <div style={{ fontSize: 13, color: C.textSec }}>{selectedItem.lastVerified || 'Never'}</div>
              </div>

              <div className="do-field">
                <label className="do-field-label">Notes</label>
                <textarea
                  className="do-textarea"
                  rows={4}
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                />
              </div>

              <button
                className="do-save-btn"
                onClick={handleSaveDetail}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── HELPER COMPONENTS ──

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div className="do-metric-card">
      <div style={{ fontSize: 24, fontWeight: 700, color, fontFamily: "'Playfair Display', serif" }}>{value}</div>
      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );
}

const POLICY_ROUTES: Record<string, string> = {
  'pol-privacy': '/privacy',
  'pol-tos': '/terms',
};

function PoliciesTab({ policies, complianceItems }: { policies: RegulatoryPolicy[]; complianceItems: ComplianceItem[] }) {
  if (policies.length === 0) {
    return (
      <EmptyState
        icon="📜"
        title="No policies loaded"
        description="Seed regulatory data from the Seed Data page to load policy version tracking."
        note="Policies tracked: Privacy Policy, Terms of Service, Consumer Health Data Policy."
      />
    );
  }

  return (
    <div>
      <div className="do-consent-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
        {policies.map((pol) => {
          const route = POLICY_ROUTES[pol.id];
          const relatedItems = complianceItems.filter((ci) =>
            ci.policyRef.toLowerCase().includes(pol.name.toLowerCase().replace('consumer health data policy', 'consumer health data').replace('terms of service', 'terms')),
          );
          const operationalCount = relatedItems.filter((ci) => ci.status === 'operational').length;

          return (
            <div key={pol.id} className="do-consent-card" style={{ padding: 20 }}>
              {/* Policy header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{pol.name}</div>
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>ID: {pol.id}</div>
                </div>
                {pol.currentVersion ? (
                  <span className="do-pill" style={{ color: C.success, background: C.successBg }}>v{pol.currentVersion}</span>
                ) : (
                  <span className="do-pill" style={{ color: C.amber, background: C.amberBg }}>DRAFT</span>
                )}
              </div>

              {/* Metadata */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Effective</div>
                  <div style={{ fontSize: 13, color: C.textSec }}>{pol.effectiveDate || '—'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Last Updated</div>
                  <div style={{ fontSize: 13, color: C.textSec }}>{pol.lastUpdated || '—'}</div>
                </div>
              </div>

              {/* Compliance cross-reference */}
              {relatedItems.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                    Compliance Items ({operationalCount}/{relatedItems.length} operational)
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {relatedItems.map((ci) => {
                      const sc = STATUS_CONFIG[ci.status];
                      return (
                        <span key={ci.id} className="do-pill" style={{ color: sc.color, background: sc.bg, fontSize: 9 }}>
                          {ci.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Version history */}
              {pol.history.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Version History</div>
                  {pol.history.map((h) => (
                    <div key={h.version} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: C.textSec, padding: '2px 0' }}>
                      <span style={{ fontWeight: 600, color: C.text }}>v{h.version}</span>
                      <span>{h.date}</span>
                      <span style={{ color: C.textMuted }}>{h.summary}</span>
                      {h.tier && (
                        <span className="do-reg-badge">{h.tier}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Action button */}
              {route && (
                <a
                  href={route}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '6px 14px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.accent,
                    background: C.accentSoft,
                    textDecoration: 'none',
                    transition: 'opacity 0.15s',
                  }}
                >
                  View Public Page &rarr;
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RequestsTab({ requests }: { requests: DataRequest[] }) {
  if (requests.length === 0) {
    return (
      <EmptyState
        icon="\uD83D\uDD10"
        title="No data requests yet"
        description="When users submit deletion, access, or portability requests, they will appear here with SLA tracking and pipeline status."
        note="Schema ready — waiting for request intake Cloud Function."
      />
    );
  }

  const REQ_COLORS: Record<string, string> = {
    deletion: C.red, access: C.blue, portability: C.blue,
    correction: C.amber, opt_out: C.textMuted, consent_withdrawal: C.orange,
  };

  return (
    <div>
      <table className="do-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>User</th>
            <th>Status</th>
            <th>Received</th>
            <th>SLA Deadline</th>
            <th>SLA</th>
            <th>Assignee</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((req) => (
            <tr key={req.id}>
              <td>
                <span className="do-pill" style={{ color: REQ_COLORS[req.type] || C.textSec, background: `${REQ_COLORS[req.type] || C.textMuted}15` }}>
                  {req.type.replace('_', ' ')}
                </span>
              </td>
              <td style={{ fontSize: 13 }}>{req.userEmail}</td>
              <td>
                <span className="do-pill" style={{ color: C.textSec, background: C.borderLight }}>
                  {req.status}
                </span>
              </td>
              <td style={{ fontSize: 13, color: C.textMuted }}>{req.receivedAt.slice(0, 10)}</td>
              <td style={{ fontSize: 13, color: C.textMuted }}>{req.slaDeadline.slice(0, 10)}</td>
              <td>
                {req.slaBreached ? (
                  <span className="do-pill" style={{ color: C.red, background: C.redBg }}>BREACHED</span>
                ) : (
                  <span style={{ fontSize: 13, color: C.success }}>{req.slaDaysRemaining}d left</span>
                )}
              </td>
              <td style={{ fontSize: 13, color: C.textMuted }}>{req.assignedTo}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConsentTab({ records }: { records: ConsentRecord[] }) {
  if (records.length === 0) {
    return (
      <EmptyState
        icon="\u2705"
        title="No consent records yet"
        description="Consent events will be logged when the opt-in consent flow is integrated at signup and in user settings."
        note="7 consent purposes defined. Schema ready for recording."
      />
    );
  }

  // Group by purpose
  const byPurpose = records.reduce<Record<string, { granted: number; withdrawn: number }>>((acc, r) => {
    const p = r.purpose;
    if (!acc[p]) acc[p] = { granted: 0, withdrawn: 0 };
    acc[p][r.action]++;
    return acc;
  }, {});

  const PURPOSE_LABELS: Record<string, string> = {
    lab_data_collection: 'Lab Data Collection',
    health_profile: 'Health Profile',
    supplement_recommendations: 'Supplement Recs',
    provider_sharing: 'Provider Sharing',
    pharmacy_sharing: 'Pharmacy Sharing',
    marketing_email: 'Marketing Email',
    analytics: 'Usage Analytics',
  };

  return (
    <div className="do-consent-grid">
      {Object.entries(byPurpose).map(([purpose, counts]) => (
        <div key={purpose} className="do-consent-card">
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{PURPOSE_LABELS[purpose] || purpose}</div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.success }}>{counts.granted}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>Granted</div>
            </div>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: C.red }}>{counts.withdrawn}</div>
              <div style={{ fontSize: 11, color: C.textMuted }}>Withdrawn</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RetentionTab({ policies, jobs }: { policies: RetentionPolicy[]; jobs: RetentionJob[] }) {
  if (policies.length === 0 && jobs.length === 0) {
    return (
      <EmptyState
        icon="\uD83D\uDDD1\uFE0F"
        title="No retention policies configured"
        description="Retention automation will clean up chat transcripts (365d), inactive users (730d), sessions (30d), and backups (90d)."
        note="Cloud Function retention-cleanup not yet deployed."
      />
    );
  }

  return (
    <div>
      {policies.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>Retention Policies</h3>
          <div className="do-consent-grid">
            {policies.map((p) => (
              <div key={p.id} className="do-consent-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{p.target.replace('_', ' ')}</span>
                  <span className="do-pill" style={{ color: p.isActive ? C.success : C.textMuted, background: p.isActive ? C.successBg : C.borderLight }}>
                    {p.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: C.textSec }}>{p.description}</div>
                {p.retentionDays && (
                  <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>{p.retentionDays} day retention</div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {jobs.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: '20px 0 12px', fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>Job History</h3>
          <table className="do-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Status</th>
                <th>Run Date</th>
                <th>Scanned</th>
                <th>Acted</th>
                <th>Errors</th>
                <th>Duration</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id}>
                  <td style={{ fontSize: 13 }}>{j.type.replace('_', ' ')}</td>
                  <td>
                    <span className="do-pill" style={{
                      color: j.status === 'completed' ? C.success : j.status === 'failed' ? C.red : C.amber,
                      background: j.status === 'completed' ? C.successBg : j.status === 'failed' ? C.redBg : C.amberBg,
                    }}>{j.status}</span>
                  </td>
                  <td style={{ fontSize: 13, color: C.textMuted }}>{j.runDate.slice(0, 10)}</td>
                  <td style={{ fontSize: 13 }}>{j.stats.scanned}</td>
                  <td style={{ fontSize: 13 }}>{j.stats.acted}</td>
                  <td style={{ fontSize: 13, color: j.stats.errors > 0 ? C.red : C.textMuted }}>{j.stats.errors}</td>
                  <td style={{ fontSize: 13, color: C.textMuted }}>{(j.durationMs / 1000).toFixed(1)}s</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function SecurityTab({ breaches, minors }: { breaches: BreachEvent[]; minors: MinorDetection[] }) {
  if (breaches.length === 0 && minors.length === 0) {
    return (
      <EmptyState
        icon="\uD83D\uDD12"
        title="No security events recorded"
        description="Breach detection scans every 6 hours for mass data access, unauthorized PHI access, unusual logins, and data exfiltration patterns."
        note="Cloud Functions breach-scan and detect-minors not yet deployed."
      />
    );
  }

  const SEV_COLORS: Record<string, { color: string; bg: string }> = {
    critical: { color: C.red, bg: C.redBg },
    high: { color: C.orange, bg: C.orangeBg },
    medium: { color: C.amber, bg: C.amberBg },
    low: { color: C.blue, bg: C.blueBg },
  };

  return (
    <div>
      {breaches.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>Breach Events</h3>
          {breaches.map((b) => {
            const sev = SEV_COLORS[b.severity] || SEV_COLORS.low;
            return (
              <div key={b.id} className="do-breach-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span className="do-pill" style={{ color: sev.color, background: sev.bg }}>{b.severity.toUpperCase()}</span>
                  <span className="do-pill" style={{ color: C.textSec, background: C.borderLight }}>{b.status}</span>
                  <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 'auto' }}>{b.detectedAt.slice(0, 10)}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>
                  {b.scope.usersAffected} users affected — {b.scope.dataCategories.join(', ')}
                </div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4 }}>
                  Detection: {b.detectionMethod.replace('_', ' ')} | Systems: {b.scope.systemsCompromised.join(', ')}
                </div>
              </div>
            );
          })}
        </>
      )}

      {minors.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: '20px 0 12px', fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>Minor Detections</h3>
          <table className="do-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Method</th>
                <th>Status</th>
                <th>Detected</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {minors.map((m) => (
                <tr key={m.id}>
                  <td style={{ fontSize: 13 }}>{m.userId}</td>
                  <td style={{ fontSize: 13 }}>{m.detectionMethod.replace('_', ' ')}</td>
                  <td>
                    <span className="do-pill" style={{
                      color: m.status === 'confirmed_minor' ? C.red : m.status === 'false_positive' ? C.success : C.amber,
                      background: m.status === 'confirmed_minor' ? C.redBg : m.status === 'false_positive' ? C.successBg : C.amberBg,
                    }}>{m.status.replace('_', ' ')}</span>
                  </td>
                  <td style={{ fontSize: 13, color: C.textMuted }}>{m.detectedAt.slice(0, 10)}</td>
                  <td style={{ fontSize: 13 }}>{m.actionTaken || '--'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function AuditTab({ entries, shares }: { entries: AuditLogEntry[]; shares: ThirdPartyShare[] }) {
  if (entries.length === 0 && shares.length === 0) {
    return (
      <EmptyState
        icon="\uD83D\uDDC2\uFE0F"
        title="No audit log entries yet"
        description="System events will be logged automatically as Data Operations features are built. Every action touching user data gets logged — no exceptions."
        note="18 audit action types defined. 6-year retention per HIPAA."
      />
    );
  }

  const ACTION_COLORS: Record<string, string> = {
    'data.view': C.blue, 'data.export': C.blue, 'data.modify': C.amber,
    'data.delete': C.red, 'data.share': C.orange,
    'auth.login': C.success, 'auth.logout': C.textMuted, 'auth.failed': C.red,
    'consent.granted': C.success, 'consent.withdrawn': C.orange,
    'phi.access': C.amber, 'phi.transmit': C.orange, 'phi.disclose': C.red,
  };

  return (
    <div>
      {entries.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12, fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>Audit Log</h3>
          <table className="do-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Action</th>
                <th>Actor</th>
                <th>Resource</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td style={{ fontSize: 12, color: C.textMuted, whiteSpace: 'nowrap' }}>{e.timestamp.slice(0, 19).replace('T', ' ')}</td>
                  <td>
                    <span className="do-pill" style={{
                      color: ACTION_COLORS[e.action] || C.textSec,
                      background: `${ACTION_COLORS[e.action] || C.textMuted}15`,
                    }}>{e.action}</span>
                  </td>
                  <td style={{ fontSize: 13 }}>{e.actor}</td>
                  <td style={{ fontSize: 12, color: C.textMuted, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.resource}</td>
                  <td>
                    <span className="do-pill" style={{
                      color: e.result === 'success' ? C.success : e.result === 'denied' ? C.amber : C.red,
                      background: e.result === 'success' ? C.successBg : e.result === 'denied' ? C.amberBg : C.redBg,
                    }}>{e.result}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {shares.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: '20px 0 12px', fontFamily: "'Playfair Display', serif", color: '#C9A96E' }}>Third-Party Sharing Log</h3>
          <table className="do-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Recipient</th>
                <th>Purpose</th>
                <th>Data</th>
                <th>Legal Basis</th>
                <th>Confirmed</th>
              </tr>
            </thead>
            <tbody>
              {shares.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontSize: 12, color: C.textMuted }}>{s.timestamp.slice(0, 10)}</td>
                  <td style={{ fontSize: 13 }}>
                    {s.recipient.name}
                    {s.recipient.baaInPlace && <span className="do-reg-badge" style={{ marginLeft: 6 }}>BAA</span>}
                  </td>
                  <td style={{ fontSize: 13 }}>{s.purpose.replace('_', ' ')}</td>
                  <td style={{ fontSize: 12, color: C.textMuted }}>{s.dataCategories.join(', ')}</td>
                  <td style={{ fontSize: 12 }}>{s.legalBasis.replace('_', ' ')}</td>
                  <td>
                    {s.confirmed ? (
                      <span style={{ color: C.success, fontSize: 13 }}>Yes</span>
                    ) : (
                      <span style={{ color: C.amber, fontSize: 13 }}>Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function EmptyState({ icon, title, description, note }: { icon: string; title: string; description: string; note: string }) {
  return (
    <div className="do-empty">
      <div style={{ fontSize: 32, marginBottom: 8 }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 500 }}>{title}</div>
      <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4, maxWidth: 400, lineHeight: 1.5 }}>{description}</div>
      <div style={{ fontSize: 12, color: C.accent, marginTop: 12, fontStyle: 'italic' }}>{note}</div>
    </div>
  );
}

// ── STYLES ──
const styles = `
.do { position: relative; background: #0D0608; padding: 20px; border-radius: 12px; }

.do-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: ${C.accent};
  background: ${C.accentSoft};
}

.do-metrics {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.do-metric-card {
  background: ${C.bg};
  border: 1px solid ${C.border};
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}

.do-section {
  background: ${C.bg};
  border: 1px solid ${C.border};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.do-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
}

.do-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.do-progress-wrap {
  position: relative;
  height: 24px;
  background: ${C.borderLight};
  border-radius: 12px;
  margin-bottom: 20px;
  overflow: hidden;
}

.do-progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(90deg, ${C.success}, #34D399);
  border-radius: 12px;
  transition: width 0.5s ease;
  min-width: 2px;
}

.do-progress-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 11px;
  font-weight: 600;
  color: ${C.textSec};
}

.do-matrix { }

.do-cat-group {
  margin-bottom: 16px;
}

.do-cat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid ${C.borderLight};
  margin-bottom: 4px;
}

.do-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
}

.do-row:hover { background: ${C.bgSoft}; }
.do-row-active { background: ${C.accentSoft}; }

.do-row-label {
  flex: 1;
  font-size: 13px;
  font-weight: 500;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.do-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.do-row-regs {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.do-reg-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  color: ${C.textMuted};
  background: ${C.borderLight};
  letter-spacing: 0.3px;
}

.do-row-sla {
  font-size: 11px;
  color: ${C.textMuted};
  width: 60px;
  text-align: right;
  flex-shrink: 0;
}

.do-row-phase {
  font-size: 10px;
  font-weight: 600;
  color: ${C.textMuted};
  width: 24px;
  text-align: center;
  flex-shrink: 0;
}

.do-tabs {
  display: flex;
  gap: 2px;
  border-bottom: 1px solid ${C.border};
  margin-bottom: 20px;
}

.do-tab {
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 500;
  color: ${C.textMuted};
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.do-tab:hover { color: ${C.text}; }

.do-tab-active {
  color: ${C.accent};
  border-bottom-color: ${C.accent};
}

.do-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  color: #F5EDE8;
  background: ${C.accent};
}

.do-tab-content {
  min-height: 200px;
}

.do-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: ${C.bgSoft};
  border: 1px dashed ${C.border};
  border-radius: 12px;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.do-table th {
  text-align: left;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${C.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${C.border};
}

.do-table td {
  padding: 10px 12px;
  border-bottom: 1px solid ${C.borderLight};
  vertical-align: middle;
}

.do-table tr:hover td { background: ${C.bgSoft}; }

.do-consent-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}

.do-consent-card {
  background: ${C.bg};
  border: 1px solid ${C.border};
  border-radius: 10px;
  padding: 16px;
}

.do-breach-card {
  background: ${C.bg};
  border: 1px solid ${C.border};
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
}

/* ── DETAIL PANEL ── */
.do-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.do-panel {
  width: 440px;
  max-width: 90vw;
  background: ${C.bg};
  height: 100%;
  overflow-y: auto;
  box-shadow: -4px 0 20px rgba(0,0,0,0.4);
}

.do-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${C.border};
  position: sticky;
  top: 0;
  background: ${C.bg};
  z-index: 1;
}

.do-panel-close {
  background: none;
  border: none;
  font-size: 24px;
  color: ${C.textMuted};
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.do-panel-close:hover { color: ${C.text}; }

.do-panel-body {
  padding: 24px;
}

.do-field {
  margin-bottom: 16px;
}

.do-field-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: ${C.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.do-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.do-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${C.border};
  border-radius: 8px;
  font-size: 13px;
  background: ${C.bgSoft};
  color: ${C.text};
  outline: none;
}

.do-select:focus { border-color: ${C.accent}; }

.do-textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${C.border};
  border-radius: 8px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  background: ${C.bgSoft};
  color: ${C.text};
  outline: none;
  box-sizing: border-box;
}

.do-textarea:focus { border-color: ${C.accent}; }

.do-save-btn {
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0D0608;
  background: ${C.btn};
  cursor: pointer;
  transition: background 0.15s;
}

.do-save-btn:hover { background: ${C.btnHover}; }
.do-save-btn:disabled { opacity: 0.5; cursor: not-allowed; }

@media (max-width: 768px) {
  .do-metrics { grid-template-columns: repeat(2, 1fr); }
  .do-row-regs { display: none; }
  .do-row-sla { display: none; }
  .do-field-row { grid-template-columns: 1fr; }
}
`;
